import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { Button } from '@shared/ui/Button';
import { Progress } from '@shared/ui/Progress';
import { NoteList } from './NoteList';
import { formatMb } from './format';
import { filesFromDrop, type UploadProgress } from './upload';
import type { UploadStage } from './useRdUpload';
import { CenterRow, Drop, DropTitle, ErrorText, HiddenInput, Muted } from './styles';

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

/**
 * Зона загрузки пакета РД: перетащить архив ZIP, папку или файлы — или
 * выбрать кнопкой. Во время отправки — прогресс по файлам и мегабайтам и
 * «Прервать».
 */
export function UploadBox({
  onFiles,
  busy,
  stage,
  progress,
  warnings,
  error,
  onCancel,
  hint,
}: {
  onFiles: (files: File[]) => void;
  busy: boolean;
  stage: UploadStage | null;
  progress: UploadProgress | null;
  warnings: string[];
  error: string | null;
  onCancel: () => void;
  hint?: string;
}) {
  const [over, setOver] = useState(false);
  /** Обход брошенной папки — до него загрузка ещё не началась, но второй бросок не принимаем. */
  const [reading, setReading] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  /** dragenter/dragleave приходят и от дочерних элементов — считаем вложенность, чтобы подсветка не мигала. */
  const depth = useRef(0);
  const mounted = useRef(false);
  const zipInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);
  const filesInput = useRef<HTMLInputElement>(null);
  const locked = busy || reading;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Выбор папки — нестандартный атрибут (в типах React его нет). Ставим его в
  // callback-ref: он попадёт на поле при любом монтировании.
  const folderRef = useCallback((el: HTMLInputElement | null) => {
    folderInput.current = el;
    if (el) {
      el.setAttribute('webkitdirectory', '');
      el.setAttribute('directory', '');
    }
  }, []);

  // Во время загрузки файл, брошенный мимо зоны, браузер открыл бы вместо
  // страницы — и загрузка оборвалась бы. Запрещаем бросок на всё окно.
  useEffect(() => {
    if (!locked) return;
    const block = (e: globalThis.DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', block);
    window.addEventListener('drop', block);
    return () => {
      window.removeEventListener('dragover', block);
      window.removeEventListener('drop', block);
    };
  }, [locked]);

  const pick = (input: HTMLInputElement) => {
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (files.length === 0 || locked) return;
    setDropError(null);
    onFiles(files);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    if (locked) return;
    setDropError(null);
    setReading(true);
    filesFromDrop(e.dataTransfer)
      .then((files) => {
        if (!mounted.current) return;
        if (files.length > 0) onFiles(files);
        else setDropError('В перетащенном не нашлось файлов.');
      })
      .catch((err: unknown) => {
        if (!mounted.current) return;
        setDropError(
          `Не удалось прочитать перетащенное${err instanceof Error && err.message ? `: ${err.message}` : ''}. ` +
            'Попробуйте кнопку «Выбрать папку».',
        );
      })
      .finally(() => {
        if (mounted.current) setReading(false);
      });
  };

  const shownError = error ?? dropError;

  return (
    <Drop
      $active={over}
      aria-busy={locked}
      onDragEnter={(e) => {
        e.preventDefault();
        depth.current += 1;
        if (!locked && hasFiles(e)) setOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = locked ? 'none' : 'copy';
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={onDrop}
    >
      {busy && stage === 'upload' && progress ? (
        <>
          <DropTitle>
            Файлов {progress.done} из {progress.total} · {formatMb(progress.bytesDone)} из{' '}
            {formatMb(progress.bytesTotal)}
            {progress.failed > 0 ? ` · не ушло: ${progress.failed}` : ''}
          </DropTitle>
          <Progress
            percent={progress.bytesTotal ? (progress.bytesDone / progress.bytesTotal) * 100 : 0}
          />
          <Muted>{progress.current ?? 'завершение…'}</Muted>
          <Muted>
            Не закрывайте и не покидайте страницу до конца отправки — загрузка прервётся (продолжить
            можно из карточки пакета).
          </Muted>
          <CenterRow>
            <Button $variant="ghost" $size="sm" onClick={onCancel}>
              Прервать
            </Button>
          </CenterRow>
        </>
      ) : locked ? (
        <>
          <DropTitle>
            {reading
              ? 'Читаем папку…'
              : stage === 'finish'
                ? 'Ставим пакет в очередь распознавания…'
                : 'Читаем архив и готовим загрузку…'}
          </DropTitle>
          {busy && stage === 'prepare' ? (
            <CenterRow>
              <Button $variant="ghost" $size="sm" onClick={onCancel}>
                Прервать
              </Button>
            </CenterRow>
          ) : null}
        </>
      ) : (
        <>
          <DropTitle>Перетащите сюда архив ZIP или папку с рабочей документацией</DropTitle>
          <Muted>
            {hint ??
              'Программа сама разложит файлы по титулам, комплектам (маркам) и ревизиям — по шифру в имени файла и основной надписи. Вы проверите раскладку и подтвердите.'}
          </Muted>
          <CenterRow>
            <Button $variant="primary" onClick={() => zipInput.current?.click()}>
              Выбрать ZIP-архив
            </Button>
            <Button $variant="secondary" onClick={() => folderInput.current?.click()}>
              Выбрать папку
            </Button>
            <Button $variant="ghost" onClick={() => filesInput.current?.click()}>
              Выбрать файлы
            </Button>
          </CenterRow>
        </>
      )}
      {/* Поля выбора смонтированы всегда: иначе после загрузки «Выбрать папку»
          терял бы атрибут выбора каталога. */}
      <HiddenInput
        ref={zipInput}
        type="file"
        accept=".zip"
        tabIndex={-1}
        onChange={(e) => pick(e.currentTarget)}
      />
      <HiddenInput
        ref={folderRef}
        type="file"
        multiple
        tabIndex={-1}
        onChange={(e) => pick(e.currentTarget)}
      />
      <HiddenInput
        ref={filesInput}
        type="file"
        multiple
        tabIndex={-1}
        onChange={(e) => pick(e.currentTarget)}
      />
      <NoteList items={warnings} />
      {shownError ? <ErrorText>{shownError}</ErrorText> : null}
    </Drop>
  );
}
